import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PrimaryButton from "../../../../../Components/Atoms/Buttons/Primary/PrimaryButton";
import config from "../../../../../config";
import { BaseURL } from "../../../../../Hooks/URL";

function ReviewModal({
  open,
  onClose,
  onConfirm,
  questions,
  mcqAnswers,
  reviewData,
  isSubmitting,
}) {
  // Get selected answer for a question
  const getSelectedAnswer = (questionId, questionIndex) => {
    const answer =  mcqAnswers[questionId] || null;
    return answer;
  };

  // Get answer text by symbol
  const getAnswerText = (question, answerSymbol) => {
    if (!answerSymbol) return null;
    const answer = question.answers?.find((a) => a.symbol === answerSymbol);
    return answer?.text || answerSymbol;
  };

  // Use review data from API if available, otherwise calculate from local state
  const answeredCount = reviewData
    ? reviewData.totalAnswerdQuestions || 0
    : questions.filter((q, index) => {
        const questionId = q.id ?? index;
        const answer = getSelectedAnswer(questionId, index);
        return answer && answer !== "";
      }).length;

  // Get questions list - use reviewData if available, otherwise use questions prop
  const displayQuestions = reviewData?.questions || questions || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          color: config.PRIMARY_COLOR,
          borderBottom: "1px solid #e5e7eb",
          pb: 2,
        }}
      >
        Review Your Answers
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            p: 3,
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          <Box sx={{ mb: 3, px: 2, bgcolor: "#f9fafb", borderRadius: 2 }}>
            <Typography sx={{ fontSize: 14, color: "#6b7280", mb: 1 }}>
              Summary
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
              {answeredCount} of {displayQuestions.length} questions answered
            </Typography>
            {answeredCount < displayQuestions.length && (
              <Typography sx={{ fontSize: 13, color: "#6b7280", mt: 0.5 }}>
                You can submit without answering all questions
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {displayQuestions.map((question, index) => {
              const questionId = question.id ?? index;

              // If reviewData is available, use it to get the selected answer
              let selectedAnswer = null;
              let answerText = null;

              if (reviewData && reviewData.mcqAnswers) {
                const reviewAnswer = reviewData.mcqAnswers.find(
                  (a) => a.questionId === questionId || a.questionId === index
                );
                if (
                  reviewAnswer &&
                  reviewAnswer.answerId !== null &&
                  reviewAnswer.answerId !== undefined
                ) {
                  // Find the answer object by answerId
                  const answerObj = question.answers?.find(
                    (a) => a.id === reviewAnswer.answerId
                  );
                  if (answerObj) {
                    selectedAnswer = answerObj.symbol || answerObj.id;
                    answerText = answerObj.text;
                  } else if (
                    question.answers &&
                    question.answers[reviewAnswer.answerId]
                  ) {
                    // Fallback to index-based lookup
                    const answerByIndex =
                      question.answers[reviewAnswer.answerId];
                    selectedAnswer =
                      answerByIndex.symbol || reviewAnswer.answerId;
                    answerText = answerByIndex.text;
                  }
                }
              } else {
                // Fallback to local state
                selectedAnswer = getSelectedAnswer(questionId, index);
                answerText = selectedAnswer
                  ? getAnswerText(question, selectedAnswer)
                  : null;
              }

              return (
                <Paper
                  key={questionId}
                  elevation={1}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    <Box
                      sx={{
                        minWidth: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: config.PRIMARY_COLOR,
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 16,
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      {question.imagePath && (
                        <img
                          src={`${BaseURL}/${question.imagePath}`}
                          alt="Question"
                          style={{
                            maxHeight: "200px",
                            maxWidth: "100%",
                            marginBottom: "12px",
                            borderRadius: "4px",
                          }}
                        />
                      )}
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: 16,
                          fontWeight: 600,
                          mb: 2,
                          color: "#111827",
                        }}
                      >
                        {question.text || `Question ${index + 1}`}
                      </Typography>

                      {selectedAnswer ? (
                        <Box
                          sx={{
                            p: 2,
                            bgcolor: "hsl(250 80% 95%)",
                            border: "1px solid hsl(250 80% 55%)",
                            borderRadius: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {/* <CheckCircleIcon
                            sx={{ color: "hsl(250 80% 55%)", fontSize: 20 }}
                          /> */}
                          <Box>
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: "#6b7280",
                                mb: 0.5,
                              }}
                            >
                              Your Answer:
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 15,
                                fontWeight: 600,
                                color: "hsl(250 80% 45%)",
                              }}
                            >
                              {selectedAnswer}: {answerText || selectedAnswer}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            p: 2,
                            bgcolor: "#f9fafb",
                            border: "1px solid #e5e7eb",
                            borderRadius: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: "#6b7280",
                              fontWeight: 500,
                            }}
                          >
                            ⚠ Not answered
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          p: 3,
          borderTop: "1px solid #e5e7eb",
          gap: 2,
        }}
      >
        <Button
          onClick={onClose}
          disabled={isSubmitting}
          sx={{
            textTransform: "none",
            color: "#6b7280",
            border: "1px solid #d1d5db",
            px: 3,
            "&:hover": {
              bgcolor: "#f9fafb",
            },
          }}
        >
          Cancel & Edit Answers
        </Button>
        <PrimaryButton
          classBtn={"center"}
          btnType={"button"}
          text={isSubmitting ? "Submitting..." : "Submit Answers"}
          click={onConfirm}
          disabled={isSubmitting}
          style={{
            textTransform: "capitalize",
            color: config.SECONDARY_COLOR,
            backgroundColor: config.PRIMARY_COLOR,
            fontWeight: 700,
            fontSize: 16,
            minWidth: "150px",
          }}
        />
      </DialogActions>
    </Dialog>
  );
}

export default ReviewModal;
